<script lang="ts">
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { MessagesSquare } from 'lucide-svelte';
  import { quotationsApi } from '$lib/api/quotations';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import { avatarColor, initials, relativeAge } from '$lib/utils/display';

  const { quotationId }: { quotationId: number } = $props();

  const queryClient = useQueryClient();
  const commentsQuery = createQuery(() => ({
    queryKey: ['quotation-comments', quotationId],
    queryFn: () => quotationsApi.listComments(quotationId)
  }));

  let draft = $state('');

  const postMut = createMutation(() => ({
    mutationFn: (body: string) => quotationsApi.createComment(quotationId, body),
    onSuccess: () => {
      draft = '';
      queryClient.invalidateQueries({ queryKey: ['quotation-comments', quotationId] });
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  function post() {
    const body = draft.trim();
    if (!body) return;
    postMut.mutate(body);
  }
</script>

<div class="tym-card p-[18px]">
  <div class="mb-3.5 flex items-center gap-2">
    <MessagesSquare class="h-4 w-4 text-toyama-teal" />
    <span class="font-heading text-[15px] font-bold text-navy">Conversation</span>
  </div>

  <div class="mb-3.5 flex flex-col gap-3">
    {#if commentsQuery.isLoading}
      <p class="tym-loading-state">Loading&hellip;</p>
    {:else if commentsQuery.isError}
      <p class="tym-error-state">{getErrorMessage(commentsQuery.error)}</p>
    {:else if (commentsQuery.data?.results.length ?? 0) === 0}
      <div class="py-2 text-center text-[12.5px] text-dark-gray">No messages yet.</div>
    {:else}
      {#each commentsQuery.data?.results ?? [] as m (m.id)}
        <div class="flex gap-2.5">
          <span
            class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
            style="background:{avatarColor(m.author_name)}"
          >
            {initials(m.author_name)}
          </span>
          <div class="min-w-0">
            <div class="flex items-baseline gap-2">
              <span class="text-[12.5px] font-semibold text-navy">{m.author_name}</span>
              <span class="text-[11px] text-dark-gray">{relativeAge(m.created_at)}</span>
            </div>
            <div class="mt-0.5 text-[13px] leading-[1.45] text-toyama-heavy-carbon">{m.body}</div>
          </div>
        </div>
      {/each}
    {/if}
  </div>

  <textarea
    class="pq-in w-full resize-none rounded-[9px] border border-toyama-cloudy-gray px-[11px] py-[9px] text-[13px] outline-none"
    placeholder="Add a note for the team…"
    rows="2"
    bind:value={draft}
  ></textarea>
  <button
    type="button"
    class="btn-secondary mt-2"
    disabled={postMut.isPending || !draft.trim()}
    onclick={post}
  >
    Post note
  </button>
</div>
